import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, CircularProgress, Stack, Typography as MuiTypography } from "@mui/material";
import { Typography as FoodsTypography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import {
    TransportLeg,
    TransportLegWrite,
    useTransportLegs,
    useUpsertTransportLeg,
} from "src/api/client-api/transport/useTransportLegs";
import { isTransportMutationContractError } from "src/api/client-api/transport/TransportMutationContractError";
import {
    type TransportRoutePreview,
    type TransportRoutePreviewInput,
    useComputeTransportRoutePreview,
} from "src/api/client-api/transport/useComputeTransportRoutePreview";
import { useGeocodeAddress } from "src/api/external/useGeocodeAddress";
import { useGeographyByCode } from "src/api/sure-api/useSearchGeographies";
import { useSearchTransportModes } from "src/api/sure-api/useSearchTransportModes";
import { useSearchTransportReferenceLegs } from "src/api/sure-api/useSearchTransportReferenceLegs";
import { EntityFormPageState, FoodsLoadingIndicator } from "src/components/EntityForm";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { greatCircleDistanceKm, type LatLng } from "src/utils/transport/greatCircle";
import { useSnackbar } from "src/utils/useSnackbar";
import { TransportLegCreateForm } from "./TransportLegCreateForm";
import { blankDraft, formatDistanceKm, type LocalDraft } from "./transportLegCreateDraft";
import { useTransportAppearance } from "./transportAppearance";

type RoutePreviewRequestSnapshot = TransportRoutePreviewInput & {
    distanceKm: string;
};

const getRoutePreviewRequestSnapshot = (draft: LocalDraft): RoutePreviewRequestSnapshot | null => {
    if (
        draft.transportModeId === null ||
        draft.originLatitude === null ||
        draft.originLongitude === null ||
        draft.destinationLatitude === null ||
        draft.destinationLongitude === null
    ) {
        return null;
    }

    return {
        transportModeId: draft.transportModeId,
        originLatitude: draft.originLatitude,
        originLongitude: draft.originLongitude,
        destinationLatitude: draft.destinationLatitude,
        destinationLongitude: draft.destinationLongitude,
        distanceKm: draft.distanceKm,
    };
};

const isSameRoutePreviewRequestSnapshot = (
    left: RoutePreviewRequestSnapshot,
    right: RoutePreviewRequestSnapshot | null,
): boolean =>
    right !== null &&
    left.transportModeId === right.transportModeId &&
    left.originLatitude === right.originLatitude &&
    left.originLongitude === right.originLongitude &&
    left.destinationLatitude === right.destinationLatitude &&
    left.destinationLongitude === right.destinationLongitude &&
    left.distanceKm === right.distanceKm;

export const TransportLegCreatePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setSnackbarProps } = useSnackbar();
    const appearance = useTransportAppearance("product-v2");
    const { legId } = useParams<{ legId?: string }>();
    const { selectedCompany } = useFoodpilotContext();
    const organizationId = selectedCompany?.id;
    const editingLegId = legId !== undefined ? Number.parseInt(legId, 10) : undefined;
    const isEditMode = editingLegId !== undefined && !Number.isNaN(editingLegId);
    const upsertLeg = useUpsertTransportLeg();
    const computeRoutePreview = useComputeTransportRoutePreview();
    const {
        transportLegs: loadedTransportLegs,
        isError: isEditingLegError,
        isLoading: isEditingLegLoading,
    } = useTransportLegs({
        enabled: organizationId !== undefined && isEditMode,
        organizationId,
    });
    const transportLegs = useMemo(() => loadedTransportLegs ?? [], [loadedTransportLegs]);
    const editingLeg = useMemo(
        () => transportLegs.find((leg) => leg.id === editingLegId) ?? null,
        [editingLegId, transportLegs],
    );
    const isEditingLegNotFound = isEditMode && !isEditingLegLoading && !isEditingLegError && editingLeg === null;
    const { transportModes } = useSearchTransportModes({ enabled: organizationId !== undefined });
    const { transportReferenceLegs } = useSearchTransportReferenceLegs({ enabled: organizationId !== undefined });
    const [draft, setDraft] = useState<LocalDraft>(blankDraft);
    const [linkedRefId, setLinkedRefId] = useState<number | null>(null);
    const [routePreview, setRoutePreview] = useState<TransportRoutePreview | null>(null);
    const [hydratedEditingLegKey, setHydratedEditingLegKey] = useState<string | null>(null);
    const [routeInputsChanged, setRouteInputsChanged] = useState(false);
    const [manualDistanceEdited, setManualDistanceEdited] = useState(false);
    const [selectedTab, setSelectedTab] = useState<"form" | "scores">("form");
    const routePreviewRequestSequenceRef = useRef(0);
    const routePreviewRequestSnapshotRef = useRef<RoutePreviewRequestSnapshot | null>(null);
    routePreviewRequestSnapshotRef.current = getRoutePreviewRequestSnapshot(draft);
    const isEditingLinked = editingLeg?.linkMode === "LINKED";
    const isReferenceLocked = linkedRefId !== null || isEditingLinked;
    const editingLegKey = editingLeg && editingLeg.id === editingLegId ? `${organizationId}:${editingLeg.id}` : null;
    const isEditingLegHydrated = editingLegKey !== null && hydratedEditingLegKey === editingLegKey;

    const { geography: originGeography } = useGeographyByCode(draft.originCountryCode, {
        enabled: organizationId !== undefined,
    });
    const { geography: destinationGeography } = useGeographyByCode(draft.destinationCountryCode, {
        enabled: organizationId !== undefined,
    });
    const hasAddressCoordinates =
        draft.originLatitude != null &&
        draft.originLongitude != null &&
        draft.destinationLatitude != null &&
        draft.destinationLongitude != null;
    const { data: originGeographyResults = [] } = useGeocodeAddress(originGeography?.name ?? "", {
        enabled: !isReferenceLocked && !hasAddressCoordinates && originGeography?.type === "country",
        delayMs: 0,
        limit: 1,
    });
    const { data: destinationGeographyResults = [] } = useGeocodeAddress(destinationGeography?.name ?? "", {
        enabled: !isReferenceLocked && !hasAddressCoordinates && destinationGeography?.type === "country",
        delayMs: 0,
        limit: 1,
    });

    useEffect(() => {
        if (!isEditMode) {
            if (hydratedEditingLegKey !== null) {
                setHydratedEditingLegKey(null);
                setRouteInputsChanged(false);
                setManualDistanceEdited(false);
            }
            return;
        }

        if (isEditingLegLoading || (isEditingLegError && editingLeg === null) || isEditingLegNotFound) return;
        if (!editingLeg || editingLegKey === null) return;
        if (hydratedEditingLegKey === editingLegKey) return;

        routePreviewRequestSequenceRef.current += 1;
        setDraft({
            name: editingLeg.name ?? "",
            transportModeId: editingLeg.transportModeId ?? null,
            distanceKm:
                editingLeg.distanceKm !== undefined && editingLeg.distanceKm !== null ?
                    String(editingLeg.distanceKm)
                :   "",
            originCountryCode: editingLeg.originCountryCode ?? "",
            destinationCountryCode: editingLeg.destinationCountryCode ?? "",
            isRefrigerated: editingLeg.isRefrigerated,
            originLatitude: editingLeg.originLatitude ?? null,
            originLongitude: editingLeg.originLongitude ?? null,
            originLabel: editingLeg.originLabel ?? "",
            destinationLatitude: editingLeg.destinationLatitude ?? null,
            destinationLongitude: editingLeg.destinationLongitude ?? null,
            destinationLabel: editingLeg.destinationLabel ?? "",
        });
        setLinkedRefId(null);
        const persistedDistanceKm = editingLeg.distanceKm;
        const persistedRouteGeometry = editingLeg.routeGeometry;
        setRoutePreview(
            persistedDistanceKm != null && persistedRouteGeometry != null ?
                {
                    distanceKm: persistedDistanceKm,
                    distanceMode: editingLeg.distanceMode ?? "COMPUTED_FROM_ROUTE",
                    routeGeometry: persistedRouteGeometry,
                }
            :   null,
        );
        setHydratedEditingLegKey(editingLegKey);
        setRouteInputsChanged(false);
        setManualDistanceEdited(editingLeg.distanceMode === "MANUAL");
    }, [
        editingLeg,
        editingLegKey,
        hydratedEditingLegKey,
        isEditingLegError,
        isEditingLegLoading,
        isEditingLegNotFound,
        isEditMode,
    ]);

    useEffect(() => {
        if (isReferenceLocked) return;
        if (routePreview !== null) return;
        if (isEditMode && !routeInputsChanged) return;
        if (manualDistanceEdited) return;

        const addressOrigin: LatLng | null =
            draft.originLatitude != null && draft.originLongitude != null ?
                [draft.originLatitude, draft.originLongitude]
            :   null;
        const addressDestination: LatLng | null =
            draft.destinationLatitude != null && draft.destinationLongitude != null ?
                [draft.destinationLatitude, draft.destinationLongitude]
            :   null;
        const geographyOrigin = originGeographyResults[0];
        const geographyDestination = destinationGeographyResults[0];
        const origin: LatLng | null =
            addressOrigin && addressDestination ? addressOrigin
            : geographyOrigin ? [geographyOrigin.latitude, geographyOrigin.longitude]
            : null;
        const destination: LatLng | null =
            addressOrigin && addressDestination ? addressDestination
            : geographyDestination ? [geographyDestination.latitude, geographyDestination.longitude]
            : null;

        setDraft((current) => {
            const nextDistance =
                origin && destination ? formatDistanceKm(greatCircleDistanceKm(origin, destination)) : "";

            return current.distanceKm === nextDistance ? current : { ...current, distanceKm: nextDistance };
        });
    }, [
        destinationGeographyResults,
        draft.destinationLatitude,
        draft.destinationLongitude,
        draft.originLatitude,
        draft.originLongitude,
        isReferenceLocked,
        isEditMode,
        manualDistanceEdited,
        originGeographyResults,
        routeInputsChanged,
        routePreview,
    ]);

    const currentDistanceMode =
        routePreview?.distanceMode ??
        (manualDistanceEdited || draft.distanceKm === "" ? "MANUAL" : "COMPUTED_FROM_COORDS");
    const isPreciseRouteSelected = routePreview?.routeGeometry != null;
    const routeComputeLabel =
        isPreciseRouteSelected ?
            t("transport.map.compute_straight_line", "Compute straight-line distance")
        :   t("transport.map.compute_route", "Compute real route");
    const formDistanceKm = draft.distanceKm === "" ? null : Number.parseFloat(draft.distanceKm);

    const previewLeg: TransportLeg | null = useMemo(() => {
        const hasCompleteCoordinates =
            draft.originLatitude != null &&
            draft.originLongitude != null &&
            draft.destinationLatitude != null &&
            draft.destinationLongitude != null;
        if (!hasCompleteCoordinates) return null;
        return {
            id: editingLeg?.id ?? -1,
            linkMode: editingLeg?.linkMode ?? (linkedRefId === null ? "LOCAL" : "LINKED"),
            sourceReferenceLegId: editingLeg?.sourceReferenceLegId ?? linkedRefId,
            name: draft.name || null,
            transportModeId: draft.transportModeId,
            originLabel: draft.originLabel || null,
            originCountryCode: draft.originCountryCode || null,
            originLatitude: draft.originLatitude,
            originLongitude: draft.originLongitude,
            destinationLabel: draft.destinationLabel || null,
            destinationCountryCode: draft.destinationCountryCode || null,
            destinationLatitude: draft.destinationLatitude,
            destinationLongitude: draft.destinationLongitude,
            distanceKm: draft.distanceKm === "" ? null : Number.parseFloat(draft.distanceKm),
            distanceMode: currentDistanceMode,
            routeGeometry:
                routePreview?.routeGeometry ??
                (!routeInputsChanged && editingLeg?.routeGeometry !== undefined ? editingLeg.routeGeometry : null),
            isRefrigerated: draft.isRefrigerated,
        };
    }, [currentDistanceMode, draft, editingLeg, linkedRefId, routeInputsChanged, routePreview]);

    if (organizationId === undefined) {
        if (appearance === "legacy") {
            return <MuiTypography variant="body2">{t("transport.no_org", "No organization selected")}</MuiTypography>;
        }

        return (
            <EntityFormPageState>
                <FoodsTypography role="status" variant="body2">
                    {t("transport.no_org", "No organization selected")}
                </FoodsTypography>
            </EntityFormPageState>
        );
    }

    if (isEditMode && isEditingLegLoading && editingLeg === null) {
        if (appearance === "legacy") {
            return (
                <Stack alignItems="center" padding={4}>
                    <CircularProgress />
                </Stack>
            );
        }

        return (
            <EntityFormPageState>
                <Stack alignItems="center" padding={4}>
                    <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                </Stack>
            </EntityFormPageState>
        );
    }

    if (isEditMode && isEditingLegNotFound) {
        if (appearance === "legacy") {
            return (
                <MuiTypography variant="body2">
                    {t("transport.legs.not_found", "Transport leg not found")}
                </MuiTypography>
            );
        }

        return (
            <EntityFormPageState>
                <FoodsTypography role="status" variant="body2">
                    {t("transport.legs.not_found", "Transport leg not found")}
                </FoodsTypography>
            </EntityFormPageState>
        );
    }

    if (isEditMode && isEditingLegError && editingLeg === null) {
        if (appearance === "legacy") {
            return <Alert severity="error">{t("transport.legs.load_error", "Unable to load transport leg")}</Alert>;
        }

        return (
            <EntityFormPageState>
                <RemoteErrorAlert
                    message={t("transport.legs.load_error", "Unable to load transport leg")}
                    status="server-error"
                />
            </EntityFormPageState>
        );
    }

    if (isEditMode && !isEditingLegHydrated) {
        if (appearance === "legacy") {
            return (
                <Stack alignItems="center" padding={4}>
                    <CircularProgress />
                </Stack>
            );
        }

        return (
            <EntityFormPageState>
                <Stack alignItems="center" padding={4}>
                    <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                </Stack>
            </EntityFormPageState>
        );
    }

    const isLocalValid =
        draft.name.trim() !== "" &&
        draft.transportModeId !== null &&
        draft.originCountryCode !== "" &&
        draft.destinationCountryCode !== "";
    const saveDisabled = isEditingLinked || (linkedRefId === null ? !isLocalValid : false);

    const handleSave = async () => {
        if (isEditMode && editingLeg?.id !== editingLegId) return;

        const orgIri = `/api/organizations/${organizationId}` as IRI;
        const payload: TransportLegWrite & { id?: number } = {
            organization: orgIri,
            linkMode: editingLeg?.linkMode ?? (linkedRefId !== null ? "LINKED" : "LOCAL"),
            sourceReferenceLegId:
                editingLeg?.linkMode === "LINKED" ? (editingLeg.sourceReferenceLegId ?? null) : (linkedRefId ?? null),
            name: draft.name || null,
            transportModeId: draft.transportModeId ?? null,
            distanceKm: formDistanceKm,
            distanceMode:
                manualDistanceEdited ? "MANUAL"
                : editingLeg?.linkMode === "LINKED" ? (editingLeg.distanceMode ?? "COPIED_FROM_REFERENCE")
                : linkedRefId !== null ? "COPIED_FROM_REFERENCE"
                : currentDistanceMode,
            originCountryCode: draft.originCountryCode || null,
            destinationCountryCode: draft.destinationCountryCode || null,
            originLatitude: draft.originLatitude,
            originLongitude: draft.originLongitude,
            originLabel: draft.originLabel || null,
            destinationLatitude: draft.destinationLatitude,
            destinationLongitude: draft.destinationLongitude,
            destinationLabel: draft.destinationLabel || null,
            isRefrigerated: draft.isRefrigerated,
            id: editingLeg?.id,
            routeGeometry: routePreview?.routeGeometry ?? null,
        };

        try {
            await upsertLeg.mutateAsync(payload);
            navigate(URLHandler.transport.list(organizationId));
        } catch (error) {
            if (isTransportMutationContractError(error)) {
                setSnackbarProps({
                    message: t(
                        "transport.legs.contract_error_after_save",
                        "The transport leg was saved, but its response is invalid. The list was refreshed.",
                    ),
                    type: "error",
                });
                navigate(URLHandler.transport.list(organizationId));
                return;
            }

            setSnackbarProps({
                message: t("transport.legs.save_error", "Failed to save the transport leg. Please try again."),
                type: "error",
            });
        }
    };

    const clearRoutePreview = () => {
        routePreviewRequestSequenceRef.current += 1;
        setRoutePreview(null);
        setRouteInputsChanged(true);
        setManualDistanceEdited(false);
        computeRoutePreview.reset();
    };

    const handleDistanceChange = (value: string) => {
        routePreviewRequestSequenceRef.current += 1;
        setRoutePreview(null);
        setRouteInputsChanged(true);
        setManualDistanceEdited(true);
        computeRoutePreview.reset();
        setDraft((current) => ({ ...current, distanceKm: value }));
    };

    const handleComputeRoutePreview = (leg: TransportLeg) => {
        if (
            leg.transportModeId == null ||
            leg.originLatitude == null ||
            leg.originLongitude == null ||
            leg.destinationLatitude == null ||
            leg.destinationLongitude == null
        ) {
            return;
        }

        const requestInput: TransportRoutePreviewInput = {
            transportModeId: leg.transportModeId,
            originLatitude: leg.originLatitude,
            originLongitude: leg.originLongitude,
            destinationLatitude: leg.destinationLatitude,
            destinationLongitude: leg.destinationLongitude,
        };
        const requestSnapshot: RoutePreviewRequestSnapshot = {
            ...requestInput,
            distanceKm: draft.distanceKm,
        };
        const requestSequence = routePreviewRequestSequenceRef.current + 1;
        routePreviewRequestSequenceRef.current = requestSequence;

        void computeRoutePreview
            .mutateAsync(requestInput)
            .then((computedRoute) => {
                if (
                    computedRoute === undefined ||
                    requestSequence !== routePreviewRequestSequenceRef.current ||
                    !isSameRoutePreviewRequestSnapshot(requestSnapshot, routePreviewRequestSnapshotRef.current)
                ) {
                    return;
                }

                setManualDistanceEdited(false);
                setRoutePreview(computedRoute);
                setDraft((current) => ({
                    ...current,
                    distanceKm: formatDistanceKm(computedRoute.distanceKm),
                }));
            })
            .catch(() => undefined);
    };

    const computeStraightLineDistance = () => {
        const addressOrigin: LatLng | null =
            draft.originLatitude != null && draft.originLongitude != null ?
                [draft.originLatitude, draft.originLongitude]
            :   null;
        const addressDestination: LatLng | null =
            draft.destinationLatitude != null && draft.destinationLongitude != null ?
                [draft.destinationLatitude, draft.destinationLongitude]
            :   null;
        const geographyOrigin = originGeographyResults[0];
        const geographyDestination = destinationGeographyResults[0];
        const origin: LatLng | null =
            addressOrigin && addressDestination ? addressOrigin
            : geographyOrigin ? [geographyOrigin.latitude, geographyOrigin.longitude]
            : null;
        const destination: LatLng | null =
            addressOrigin && addressDestination ? addressDestination
            : geographyDestination ? [geographyDestination.latitude, geographyDestination.longitude]
            : null;

        if (!origin || !destination) return;

        routePreviewRequestSequenceRef.current += 1;
        setRoutePreview(null);
        setRouteInputsChanged(true);
        setManualDistanceEdited(false);
        setDraft((current) => ({
            ...current,
            distanceKm: formatDistanceKm(greatCircleDistanceKm(origin, destination)),
        }));
    };

    const handleMapRouteCompute = (leg: TransportLeg) => {
        if (isPreciseRouteSelected) {
            computeStraightLineDistance();
            return;
        }

        handleComputeRoutePreview(leg);
    };

    return (
        <TransportLegCreateForm
            destinationGeography={destinationGeography}
            draft={draft}
            editingLeg={editingLeg}
            formDistanceKm={formDistanceKm}
            isEditingLegError={isEditingLegError}
            isEditingLinked={isEditingLinked}
            isEditMode={isEditMode}
            isReferenceLocked={isReferenceLocked}
            linkedRefId={linkedRefId}
            organizationId={organizationId}
            originGeography={originGeography}
            previewLeg={previewLeg}
            routeComputeError={computeRoutePreview.isError ? computeRoutePreview.error : undefined}
            routeComputeLabel={routeComputeLabel}
            routeComputeLoading={computeRoutePreview.isLoading}
            saveDisabled={saveDisabled}
            saveLoading={upsertLeg.isLoading}
            selectedTab={selectedTab}
            transportModes={transportModes}
            transportReferenceLegs={transportReferenceLegs}
            onClearRoutePreview={clearRoutePreview}
            onDistanceChange={handleDistanceChange}
            onMapRouteCompute={handleMapRouteCompute}
            onSave={handleSave}
            setDraft={setDraft}
            setLinkedRefId={setLinkedRefId}
            setSelectedTab={setSelectedTab}
        />
    );
};

export default TransportLegCreatePage;
